import { Injectable, inject } from '@angular/core';

import { LogoutService } from '../logout/logout.service';
import { PlatformService } from '../platform/platform.service';

import {
  CleanupList,
  KeysToCleanup,
  STORAGE_CLEANUP_CONFIG_KEY,
  StorageConfig,
  StorageHelperService,
  convertToCleanupList,
  getAllPrefixedKeys,
  getPrefixedKey
} from './storage-helper.service';
import { StorageType } from './storage.service';

/**
 * StorageCleanupService takes care for cleaning up keys from browser storage.
 * It will be configured successively by each individual instance of StorageService,
 * which can be provided and configured via StorageModule.forChild().
 * This config includes a storage prefix as well as if the keys should be deleted
 * on logout and/or account switch.
 * The configs provided via StorageModule.forChild() will be collected and stored
 * in localStorage, so they will be always available independent of current app state.
 */

@Injectable({
  providedIn: 'root'
})
export class StorageCleanupService {
  private readonly logoutService = inject(LogoutService);
  private readonly storageHelper = inject(StorageHelperService);
  private readonly localStorage = this.storageHelper.getStorage(StorageType.LOCAL);
  private readonly sessionStorage = this.storageHelper.getStorage(StorageType.SESSION);

  private storageConfig: StorageConfig;

  constructor() {
    if (inject(PlatformService).isBrowser) {
      this.cleanUpOnLogout();
      this.cleanUpOnAccountSwitch();
    }
  }

  public setConfig(config: StorageConfig): void {
    this.storageConfig = config;
  }

  /**
   * Remove storage keys on logout (no matter if session or local storage),
   * if applied StorageConfig has related keys defined.
   * Either an Array of keys can be applied, or true. If true, then storage prefix
   * will be used for filtering all keys starting with the prefix.
   * In addition, a specific regex pattern can be used for filtering the prefixed keys.
   */
  private cleanUpOnLogout(): void {
    this.logoutService.logout$().subscribe(() => this.performCleanup(convertToCleanupList(this.storageConfig, 0)));
  }

  /**
   * Same as above but on account switch
   */
  private cleanUpOnAccountSwitch(): void {
    this.logoutService
      .switchAccount$()
      .subscribe(() => this.performCleanup(convertToCleanupList(this.storageConfig, 1)));
  }

  private performCleanup(config: CleanupList): void {
    config.forEach(([prefix, keys, pattern]) => this.cleanupKeys(prefix, keys, pattern && new RegExp(pattern)));
  }

  private cleanupKeys(prefix: string, keysToCleanup: KeysToCleanup, excludePattern: RegExp): void {
    [this.localStorage, this.sessionStorage].filter(Boolean).forEach(storage => {
      let keys = Array.isArray(keysToCleanup)
        ? keysToCleanup.map(key => getPrefixedKey(key, prefix))
        : getAllPrefixedKeys(storage, prefix);

      if (excludePattern) {
        keys = keys.filter(key => !excludePattern.test(key));
      }

      keys
        .filter(k => k !== STORAGE_CLEANUP_CONFIG_KEY)
        .forEach(key => {
          storage.removeItem(key);
        });
    });
  }
}
